import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { api, errorMessage } from '../api.js'
import { useToast } from '../../components/ui/Toast.jsx'
import { qk } from './keys.js'
import { useMe } from './session.js'

export const projectsQuery = queryOptions({
  queryKey: qk.projects,
  queryFn: ({ signal }) => api('/projects', { signal }).then((d) => d.projects),
  staleTime: 30_000,
})

export function useProjects() {
  return useQuery(projectsQuery)
}

/** Editors and admins can change projects; everyone else reads. */
export function useCanEditProjects() {
  const { user } = useMe()
  return user?.role === 'admin' || user?.role === 'editor'
}

/**
 * Every project write returns the whole updated project, which replaces the
 * cached copy. Deleting a project passes `removeId` instead.
 */
function useProjectRequest() {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: ({ path = '', method = 'POST', body }) => api(`/projects${path}`, { method, body }),
    onSuccess: (data, vars) => {
      const project = data?.project
      queryClient.setQueryData(qk.projects, (list = []) => {
        if (project) return list.some((p) => p.id === project.id) ? list.map((p) => (p.id === project.id ? project : p)) : [project, ...list]
        if (vars.removeId) return list.filter((p) => p.id !== vars.removeId)
        return list
      })
      if (vars.success) toast.success(vars.success)
    },
    onError: (error) => {
      toast.error(errorMessage(error, 'Could not save that change.'))
      queryClient.invalidateQueries({ queryKey: qk.projects })
    },
  })
}

/** Named project writes. Each returns a promise of the updated project (or null after a delete). */
export function useProjectApi() {
  const request = useProjectRequest()
  const send = (vars) => request.mutateAsync(vars).then((d) => d?.project ?? null).catch(() => null)
  return useMemo(
    () => ({
      saving: request.isPending,
      create: (body) => send({ body, success: 'Project created' }),
      patch: (id, body, success) => send({ path: `/${id}`, method: 'PATCH', body, success }),
      remove: (id) => send({ path: `/${id}`, method: 'DELETE', removeId: id, success: 'Project deleted' }),
      addSystem: (id, body) => send({ path: `/${id}/systems`, body, success: 'System added with its checklist' }),
      patchSystem: (id, sid, body) => send({ path: `/${id}/systems/${sid}`, method: 'PATCH', body }),
      removeSystem: (id, sid) => send({ path: `/${id}/systems/${sid}`, method: 'DELETE' }),
      addCredential: (id, sid, body) => send({ path: `/${id}/systems/${sid}/credentials`, body }),
      patchCredential: (id, cid, body) => send({ path: `/${id}/credentials/${cid}`, method: 'PATCH', body }),
      removeCredential: (id, cid) => send({ path: `/${id}/credentials/${cid}`, method: 'DELETE' }),
      addAction: (id, body) => send({ path: `/${id}/actions`, body, success: 'Action item added' }),
      patchAction: (id, aid, body) => send({ path: `/${id}/actions/${aid}`, method: 'PATCH', body }),
      removeAction: (id, aid) => send({ path: `/${id}/actions/${aid}`, method: 'DELETE' }),
      moveAction: (id, weekOf, actionId) => send({ path: `/${id}/actions/from-call`, body: { weekOf, actionId }, success: 'Moved to Overview' }),
      syncSybill: (id) => send({ path: `/${id}/sybill/sync`, success: 'Checked Sybill for new meetings' }),
      saveCall: (id, week, body, success) => send({ path: `/${id}/calls/${week}`, method: 'PUT', body, success }),
      removeCall: (id, week) => send({ path: `/${id}/calls/${week}`, method: 'DELETE', success: 'Call record deleted' }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [request.mutateAsync, request.isPending],
  )
}
